import React, { useState } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { candidatesAPI, assessmentsAPI, interviewsAPI, credentialsAPI } from '../../lib/api';
import { getScoreClass, getScoreColor } from '../../lib/scoreUtils';

const primaryBtn = { background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.8125rem', fontWeight: 600 };

export default function CandidateHeader({ candidate, candidateId, parsed, onBack, onVerify, loadCandidate, analyzingDepth, depthError, onAnalyze, onClearError }) {
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({ name: '', email: '', phone: '', location: '' });
  const [savingDetails, setSavingDetails] = useState(false);
  const [reparsing, setReparsing] = useState(false);

  const openEditDetails = () => {
    const p = candidate.parsed_resume || {};
    setDetailsForm({ name: candidate.name || '', email: p.email || '', phone: p.phone || '', location: p.location || '' });
    setEditingDetails(true);
  };

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      await candidatesAPI.updateDetails(candidateId, detailsForm);
      toast.success('Candidate details updated');
      setEditingDetails(false);
      loadCandidate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update details');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleReparse = async () => {
    setReparsing(true);
    try {
      await candidatesAPI.reparse(candidateId);
      toast.success('Re-parsing started — refreshing shortly...');
      setTimeout(() => loadCandidate(), 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to re-parse');
    } finally {
      setReparsing(false);
    }
  };

  const sendAssessment = async () => {
    try {
      const res = await assessmentsAPI.sendToCandidate(candidate.job_id, { candidate_name: candidate.name, candidate_email: parsed.email || '' });
      navigator.clipboard.writeText(`${window.location.origin}${res.data.assessment_link}`);
      toast.success(`Assessment sent to ${candidate.name}! Link copied.`);
      loadCandidate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send assessment'); }
  };

  const sendInterview = async () => {
    try {
      const res = await interviewsAPI.sendToCandidate(candidate.job_id, { candidate_name: candidate.name, candidate_email: parsed.email || '', template: 'technical' });
      navigator.clipboard.writeText(`${window.location.origin}${res.data.interview_link}`);
      toast.success(`Interview invitation sent to ${candidate.name}! Link copied.`);
      loadCandidate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send interview'); }
  };

  const issueToken = async () => {
    try {
      const res = await credentialsAPI.issue({ candidate_name: candidate.name, candidate_email: parsed.email || '', job_id: candidate.job_id, job_title: '' });
      navigator.clipboard.writeText(`${window.location.origin}/credentials/${res.data.token_id}`);
      toast.success(`NFT Credential issued! Link copied. (${res.data.readiness})`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to issue token'); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', marginBottom: '2rem' }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', padding: '0.375rem', marginTop: '0.25rem' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #0076A3, #00A0E0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
        {candidate.name?.charAt(0)?.toUpperCase()}
      </div>
      <div style={{ flex: 1 }}>
        <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>{candidate.name}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', flexWrap: 'wrap' }}>
          {parsed.email ? <span data-testid="candidate-email">{parsed.email}</span> : <span style={{ color: '#DC2626' }}>No email</span>}
          {parsed.phone ? <span data-testid="candidate-phone">&middot; {parsed.phone}</span> : <span style={{ color: '#DC2626' }}>&middot; No phone</span>}
          {parsed.location ? <span>&middot; {parsed.location}</span> : <span style={{ color: '#DC2626' }}>&middot; No location</span>}
          {parsed.total_years_experience > 0 && <span>&middot; {parsed.total_years_experience} years experience</span>}
          <button data-testid="edit-details-button" onClick={openEditDetails} style={{ background: 'none', border: '1px solid #CBD5E1', borderRadius: 6, padding: '0.125rem 0.5rem', cursor: 'pointer', fontSize: '0.75rem', color: '#0076A3', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
            Edit details
          </button>
        </div>
        {editingDetails && (
          <div data-testid="edit-details-panel" style={{ marginTop: '0.75rem', padding: '1rem', borderRadius: 12, background: '#FAF7F2', border: '1px solid #ECE7DE', maxWidth: 620 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.625rem' }}>Edit candidate contact details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.625rem' }}>
              {[['name', 'Full Name'], ['email', 'Email'], ['phone', 'Phone'], ['location', 'Location']].map(([key, label]) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: '#64748B', marginBottom: '0.25rem' }}>{label}</label>
                  <input data-testid={`edit-details-${key}`} value={detailsForm[key]} onChange={(e) => setDetailsForm({ ...detailsForm, [key]: e.target.value })} style={{ width: '100%', padding: '0.5rem 0.625rem', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: '0.8125rem', background: '#fff' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <Button data-testid="save-details-button" onClick={saveDetails} disabled={savingDetails} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 }}>{savingDetails ? 'Saving...' : 'Save'}</Button>
              <Button variant="outline" onClick={() => setEditingDetails(false)} style={{ borderRadius: 8, fontSize: '0.75rem', fontWeight: 600 }}>Cancel</Button>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
          <Button data-testid="initiate-verification-button" onClick={onVerify} style={primaryBtn}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /><path d="M9 14l2 2 4-4" /></svg>
            Initiate Verification
          </Button>
          <Button data-testid="send-assessment-button" onClick={sendAssessment} style={primaryBtn}>Send Assessment</Button>
          <Button data-testid="send-interview-button" onClick={sendInterview} style={primaryBtn}>Send Interview</Button>
          <Button data-testid="issue-nft-button" onClick={issueToken} style={primaryBtn}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
            Issue NFT Token
          </Button>
          <Button data-testid="analyze-depth-button" disabled={analyzingDepth} onClick={onAnalyze} style={{ background: analyzingDepth ? '#92400E' : '#D97706', color: '#fff', borderRadius: 10, fontSize: '0.8125rem', fontWeight: 600 }}>
            {analyzingDepth ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}><span className="status-dot status-processing" style={{ width: 8, height: 8 }} />Analyzing Resume...</span>
            ) : 'Analyze Depth'}
          </Button>
          <Button data-testid="reparse-resume-button" onClick={handleReparse} disabled={reparsing} variant="outline" style={{ borderRadius: 10, fontSize: '0.8125rem', fontWeight: 600, borderColor: '#0076A3', color: '#0076A3' }}>
            {reparsing ? 'Re-parsing...' : 'Re-parse Resume'}
          </Button>
        </div>
        {depthError && (
          <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '0.75rem', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{depthError}</span>
            <button onClick={onClearError} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>&times;</button>
          </div>
        )}
      </div>
      {candidate.overall_score !== null && candidate.overall_score !== undefined && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', border: `3px solid ${getScoreColor(candidate.overall_score)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: getScoreColor(candidate.overall_score) }}>
            {candidate.overall_score}
          </div>
          <div className={`score-badge ${getScoreClass(candidate.overall_score)}`} style={{ marginTop: '0.5rem', fontSize: '0.6875rem' }}>{candidate.fit_recommendation}</div>
        </div>
      )}
    </div>
  );
}
